<template>
    <div>
        <!-- Trigger Button -->
        <!-- <button class="btn-duplicate" @click="openDuplicateModal(order)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Duplicate Order
        </button> -->

        <!-- Modal Overlay -->
        <transition name="modal-fade">
            <div v-if="duplicateModal.show" class="modal-overlay" @click.self="closeDuplicateModal">
                <div class="modal-card">

                    <!-- Header -->
                    <div class="modal-header">
                        <div class="modal-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        </div>
                        <div>
                            <h2 class="modal-title">Duplicate Order</h2>
                            <p class="modal-subtitle">Cloning from <strong>{{ duplicateModal.sourceOrderId }}</strong>
                            </p>
                        </div>
                        <button class="modal-close" @click="closeDuplicateModal">✕</button>
                    </div>

                    <!-- Body -->
                    <div class="modal-body">

                        <!-- Client Selector -->
                        <div class="field-group">
                            <label class="field-label">
                                Select Sub Vendor
                                <span class="field-required">*</span>
                            </label>
                            <div class="select-wrapper">
                                <select v-model="duplicateModal.selectedClientId" class="field-select"
                                    :class="{ 'has-error': duplicateModal.errors.client }" @change="onClientChange">
                                    <option value="" disabled>— Choose a client —</option>
                                    <option v-for="client in clients" :key="client.id" :value="client.id">
                                        {{ client.name }}
                                    </option>
                                </select>
                                <span class="select-arrow">▾</span>
                            </div>
                            <p v-if="duplicateModal.errors.client" class="error-msg">{{ duplicateModal.errors.client }}
                            </p>
                        </div>

                        <!-- Order Number -->
                        <div class="field-group">
                            <label class="field-label">
                                New Order Number
                                <span class="field-required">*</span>
                            </label>
                            <div class="input-wrapper">
                                <input v-model.trim="duplicateModal.newOrderNumber" type="text" class="field-input"
                                    :class="{ 'has-error': duplicateModal.errors.orderNumber }"
                                    placeholder="e.g. ORD-2024-099" @input="clearFieldError('orderNumber')" />
                                <span v-if="duplicateModal.checking" class="input-spinner">⟳</span>
                                <span v-else-if="duplicateModal.orderNumberValid === true" class="input-check">✓</span>
                            </div>
                            <p v-if="duplicateModal.errors.orderNumber" class="error-msg">{{
                                duplicateModal.errors.orderNumber }}</p>
                            <p v-else class="field-hint">Must be unique for the selected client.</p>
                        </div>

                        <!-- Subcollection Preview — shows what will be copied -->
                        <transition name="slide-down">
                            <div v-if="duplicateModal.subcollections.length" class="subcollection-info">
                                <div class="subcollection-header">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                    </svg>
                                    Subcollections to copy
                                </div>
                                <div class="subcollection-tags">
                                    <span v-for="sub in duplicateModal.subcollections" :key="sub.name"
                                        class="subcollection-tag">
                                        {{ sub.name }}
                                        <em>{{ sub.count }} doc{{ sub.count !== 1 ? 's' : '' }}</em>
                                    </span>
                                </div>
                            </div>
                        </transition>

                        <!-- Conflict Warning -->
                        <transition name="slide-down">
                            <div v-if="duplicateModal.conflictWarning" class="conflict-banner">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <path
                                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                {{ duplicateModal.conflictWarning }}
                            </div>
                        </transition>

                    </div>

                    <!-- Footer -->
                    <div class="modal-footer">
                        <button class="btn-cancel" @click="closeDuplicateModal" :disabled="duplicateModal.saving">
                            Cancel
                        </button>
                        <button class="btn-save" @click="saveDuplicateOrder"
                            :disabled="duplicateModal.saving || duplicateModal.checking">
                            <span v-if="duplicateModal.saving" class="spinner">⟳</span>
                            <span v-else>Save Duplicate</span>
                        </button>
                    </div>

                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { db } from "@/config/firebaseInit.js";

// ─── Register your subcollection names here ───────────────────────────────────
// Add/remove to match your Firestore schema under each order doc.
// e.g. ['items', 'notes', 'attachments', 'payments', 'history']
const ORDER_SUBCOLLECTIONS = ['items', 'line_items', 'quality_control']

export default {
    name: 'DuplicateOrderModal',

    props: {
        order: {
            type: Object,
            required: true
        },
        clients: {
            type: Array,
            default: () => []
        },
        modelValue: { type: Boolean, default: false }
    },

    data() {
        return {
            duplicateModal: {
                show: false,
                saving: false,
                checking: false,
                sourceOrderId: '',
                sourceData: null,
                subcollections: [],   // [{ name, docs: [{ id, data }], count }]
                selectedClientId: '',
                newOrderNumber: '',
                orderNumberValid: null,
                conflictWarning: '',
                errors: {
                    client: '',
                    orderNumber: ''
                }
            }
        }
    },

    watch: {
        modelValue(val) {
            if (val) this.openDuplicateModal(this.order)
            else this.closeDuplicateModal()
        }
    },

    methods: {

        // ─────────────────────────────────────────────────────────────────
        // 1. OPEN — fetch parent doc + all subcollection docs in parallel
        // ─────────────────────────────────────────────────────────────────
        async openDuplicateModal(order) {
            try {
                const snap = await db.collection('orders').doc(order.id).get()

                if (!snap.exists) {
                    alert('Source order not found.')
                    return
                }

                // Fetch all known subcollections simultaneously
                const subcollections = await this.fetchSubcollections(order.id)

                this.duplicateModal = {
                    show: true,
                    saving: false,
                    checking: false,
                    sourceOrderId: order.id,
                    sourceData: snap.data(),
                    subcollections,
                    selectedClientId: '',
                    newOrderNumber: '',
                    orderNumberValid: null,
                    conflictWarning: '',
                    errors: { client: '', orderNumber: '' }
                }
            } catch (err) {
                console.error('Error fetching source order:', err)
                alert('Failed to load the order. Please try again.')
            }
        },

        // ─────────────────────────────────────────────────────────────────
        // HELPER — fetch all docs from every known subcollection
        // Skips subcollections that are empty or don't exist on this doc.
        // Returns: [{ name: 'items', docs: [{ id, data }], count: N }, ...]
        // ─────────────────────────────────────────────────────────────────
        async fetchSubcollections(orderId) {
            const results = await Promise.all(
                ORDER_SUBCOLLECTIONS.map(async (subName) => {
                    try {
                        const snap = await db
                            .collection('orders')
                            .doc(orderId)
                            .collection(subName)
                            .get()

                        if (snap.empty) return null  // skip subcollections with no docs

                        const docs = snap.docs.map(d => ({ id: d.id, data: d.data() }))
                        return { name: subName, docs, count: docs.length }
                    } catch (err) {
                        // Subcollection may not exist on this particular doc — skip silently
                        console.warn(`Subcollection "${subName}" skipped:`, err.message)
                        return null
                    }
                })
            )
            return results.filter(Boolean)
        },

        // ─────────────────────────────────────────────────────────────────
        // 2. CLOSE / RESET
        // ─────────────────────────────────────────────────────────────────
        closeDuplicateModal() {
            this.duplicateModal.show = false
            this.$emit('modalClose', false)
        },

        // ─────────────────────────────────────────────────────────────────
        // 3. CLEAR individual field error on edit
        // ─────────────────────────────────────────────────────────────────
        clearFieldError(field) {
            this.duplicateModal.errors[field] = ''
            this.duplicateModal.orderNumberValid = null
            this.duplicateModal.conflictWarning = ''
        },

        onClientChange() {
            this.duplicateModal.errors.client = ''
            this.duplicateModal.conflictWarning = ''
            this.duplicateModal.orderNumberValid = null
        },

        // ─────────────────────────────────────────────────────────────────
        // 4. VALIDATE form fields locally
        // ─────────────────────────────────────────────────────────────────
        validateDuplicateForm() {
            const m = this.duplicateModal
            let valid = true

            m.errors.client = ''
            m.errors.orderNumber = ''

            if (!m.selectedClientId) {
                m.errors.client = 'Please select a client.'
                valid = false
            }

            if (!m.newOrderNumber) {
                m.errors.orderNumber = 'Order number is required.'
                valid = false
            } else if (!/^[a-zA-Z0-9\-_]+$/.test(m.newOrderNumber)) {
                m.errors.orderNumber = 'Only letters, numbers, hyphens, and underscores are allowed.'
                valid = false
            }

            return valid
        },

        // ─────────────────────────────────────────────────────────────────
        // 5. FIRESTORE duplicate check — blocks same client + same order number
        // ─────────────────────────────────────────────────────────────────
        async checkForDuplicate(clientId, orderNumber) {
            const m = this.duplicateModal
            m.checking = true
            m.conflictWarning = ''
            m.orderNumberValid = null

            try {
                const snap = await db
                    .collection('orders')
                    .where('Client', '==', clientId)
                    .where('OrderNum', '==', orderNumber)
                    .get()

                if (!snap.empty) {
                    const clientName = this.clients.find(c => c.id === clientId)?.name || clientId
                    m.errors.orderNumber = `Order "${orderNumber}" already exists for ${clientName}.`
                    m.conflictWarning = `A duplicate was found. Please choose a different order number or a different client.`
                    m.orderNumberValid = false
                    return false
                }

                m.orderNumberValid = true
                return true
            } catch (err) {
                console.error('Duplicate check failed:', err)
                m.errors.orderNumber = 'Could not verify uniqueness. Please try again.'
                return false
            } finally {
                m.checking = false
            }
        },

        // ─────────────────────────────────────────────────────────────────
        // 6. SAVE — write parent doc + all subcollection docs in one batch
        // ─────────────────────────────────────────────────────────────────
        async saveDuplicateOrder() {
            const m = this.duplicateModal

            // Step A: Local validation
            if (!this.validateDuplicateForm()) return

            // Step B: Firestore uniqueness check
            const isUnique = await this.checkForDuplicate(m.selectedClientId, m.newOrderNumber)
            if (!isUnique) return

            // Step C: Batch write — parent doc + every subcollection doc
            m.saving = true
            try {
                const batch = db.batch()
                const newOrderRef = db.collection('orders').doc(m.newOrderNumber)

                // --- Parent doc ---
                batch.set(newOrderRef, {
                    ...m.sourceData,
                    Client: m.selectedClientId,
                    OrderNum: m.newOrderNumber,
                    FailedItems:0,
                    DuplicatedFrom: m.sourceOrderId,   // audit trail
                    Timestamp: Date.now()
                })

                // --- Subcollection docs ---
                // Docs keep their original IDs so internal references stay consistent.
                // Swap .doc(subDoc.id) → .doc() if you prefer auto-generated IDs.
                let totalSubDocs = 0
                for (const sub of m.subcollections) {
                    for (const subDoc of sub.docs) {
                        const subRef = newOrderRef.collection(sub.name).doc(subDoc.id)
                        batch.set(subRef, {
                            ...subDoc.data,
                            DuplicatedFrom: m.sourceOrderId,  // per-sub-doc audit trail
                            Timestamp: Date.now()
                        })
                        totalSubDocs++
                    }
                }

                // ⚠️ Firestore batch limit = 500 writes (1 parent + up to 499 sub-docs).
                // If you expect larger subcollections, use commitInChunks() below instead.
                await batch.commit()

                this.$emit('orderDuplicated', {
                    newId: m.newOrderNumber,
                    clientId: m.selectedClientId,
                    orderNumber: m.newOrderNumber,
                    subcollectionsCopied: m.subcollections.map(s => s.name),
                    totalDocsCopied: totalSubDocs
                })

                this.closeDuplicateModal()
                // Replace with your notification system:
                alert(`Order duplicated as "${m.newOrderNumber}" — ${totalSubDocs} sub-doc(s) copied.`)

            } catch (err) {
                console.error('Failed to save duplicate order:', err)
                m.errors.orderNumber = 'Save failed. Please check your connection and try again.'
            } finally {
                m.saving = false
            }
        },

        // ─────────────────────────────────────────────────────────────────
        // OPTIONAL HELPER — split writes into 490-item chunks
        // Use this instead of batch.commit() if sub-docs may exceed ~490.
        //
        // Usage:
        //   const writes = [{ ref: docRef, data: {...} }, ...]
        //   await this.commitInChunks(writes)
        // ─────────────────────────────────────────────────────────────────
        async commitInChunks(writes) {
            const CHUNK_SIZE = 490  // leaves headroom for the parent doc write
            for (let i = 0; i < writes.length; i += CHUNK_SIZE) {
                const batch = db.batch()
                writes.slice(i, i + CHUNK_SIZE).forEach(w => batch.set(w.ref, w.data))
                await batch.commit()
            }
        }
    }
}
</script>

<style scoped>
.btn-duplicate {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 18px;
    background: #1a1a2e;
    color: #e0e0f0;
    border: 1px solid #3a3a5c;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'DM Mono', monospace;
    cursor: pointer;
    transition: all 0.18s ease;
}

.btn-duplicate:hover {
    background: #25254a;
    border-color: #6c63ff;
    color: #fff;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(8, 8, 20, 0.72);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-card {
    background: #12122a;
    border: 1px solid #2e2e52;
    border-radius: 16px;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(108, 99, 255, 0.12);
    overflow: hidden;
}

.modal-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 22px 24px;
    border-bottom: 1px solid #1e1e3a;
}

.modal-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6c63ff22, #4f46e522);
    border: 1px solid #6c63ff44;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9d95ff;
    flex-shrink: 0;
}

.modal-title {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #e8e8f8;
    font-family: 'DM Sans', sans-serif;
}

.modal-subtitle {
    margin: 2px 0 0;
    font-size: 12px;
    color: #6666aa;
    font-family: 'DM Mono', monospace;
}

.modal-subtitle strong {
    color: #9d95ff;
}

.modal-close {
    margin-left: auto;
    background: none;
    border: none;
    color: #555580;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.15s;
}

.modal-close:hover {
    background: #1e1e3a;
    color: #aaa;
}

.modal-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7070aa;
    font-family: 'DM Sans', sans-serif;
}

.field-required {
    color: #ff6b8a;
    margin-left: 3px;
}

.select-wrapper,
.input-wrapper {
    position: relative;
}

.select-arrow {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #555580;
    pointer-events: none;
    font-size: 12px;
}

.input-check,
.input-spinner {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 15px;
}

.input-check {
    color: #4ade80;
}

.input-spinner {
    color: #9d95ff;
    animation: spin 1s linear infinite;
}

.field-select,
.field-input {
    width: 100%;
    padding: 11px 40px 11px 14px;
    background: #0d0d22;
    border: 1px solid #2a2a48;
    border-radius: 9px;
    color: #ddddf8;
    font-size: 14px;
    font-family: 'DM Mono', monospace;
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    appearance: none;
    box-sizing: border-box;
}

.field-input {
    padding-right: 40px;
}

.field-select:focus,
.field-input:focus {
    border-color: #6c63ff;
    box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.15);
}

.has-error {
    border-color: #ff6b8a !important;
    box-shadow: 0 0 0 3px rgba(255, 107, 138, 0.12) !important;
}

.error-msg {
    font-size: 12px;
    color: #ff6b8a;
    margin: 0;
    font-family: 'DM Sans', sans-serif;
}

.field-hint {
    font-size: 12px;
    color: #44447a;
    margin: 0;
    font-family: 'DM Sans', sans-serif;
}

/* ── Subcollection preview ────────────────────── */
.subcollection-info {
    padding: 12px 14px;
    background: rgba(108, 99, 255, 0.06);
    border: 1px solid rgba(108, 99, 255, 0.2);
    border-radius: 9px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.subcollection-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #7070aa;
    font-family: 'DM Sans', sans-serif;
}

.subcollection-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.subcollection-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: rgba(108, 99, 255, 0.12);
    border: 1px solid rgba(108, 99, 255, 0.25);
    border-radius: 20px;
    font-size: 12px;
    color: #9d95ff;
    font-family: 'DM Mono', monospace;
}

.subcollection-tag em {
    font-style: normal;
    color: #555580;
    font-size: 11px;
}

.conflict-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: rgba(255, 107, 138, 0.08);
    border: 1px solid rgba(255, 107, 138, 0.25);
    border-radius: 9px;
    color: #ff9ab2;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    line-height: 1.4;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 18px 24px;
    border-top: 1px solid #1e1e3a;
}

.btn-cancel {
    padding: 10px 20px;
    background: transparent;
    border: 1px solid #2a2a48;
    border-radius: 8px;
    color: #7070aa;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: all 0.15s;
}

.btn-cancel:hover:not(:disabled) {
    border-color: #444468;
    color: #aaa;
}

.btn-save {
    padding: 10px 24px;
    background: linear-gradient(135deg, #6c63ff, #4f46e5);
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: all 0.18s;
    min-width: 130px;
}

.btn-save:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(108, 99, 255, 0.4);
}

.btn-save:disabled,
.btn-cancel:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.spinner {
    display: inline-block;
    animation: spin 0.8s linear infinite;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.22s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.slide-down-enter-active {
    transition: all 0.25s ease;
}

.slide-down-leave-active {
    transition: all 0.2s ease;
}

.slide-down-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

option {
    background: #12122a;
    color: #ddddf8;
}
</style>